import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';

import { ATTACHMENT_MAX_FILE_SIZE_BYTES } from '../../constants/app-constants';

@Component({
  selector: 'app-image-crop-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './image-crop-modal.component.html',
  styleUrl: './image-crop-modal.component.css'
})
export class ImageCropModalComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() sourceFile: File | null = null;
  @Input() title = 'Crop Image';
  @Input() hint = 'Drag the image and adjust zoom to fit the profile area. The cropped image will be compressed before upload.';
  @Input() outputHint = 'The cropped image will be saved as JPG to reduce upload size.';
  @Input() confirmLabel = 'Use Cropped Image';
  @Input() cancelLabel = 'Cancel';
  @Input() fallbackBaseName = 'image';
  @Output() cancel = new EventEmitter<void>();
  @Output() confirm = new EventEmitter<File>();
  @ViewChild('cropViewport') cropViewportRef?: ElementRef<HTMLDivElement>;

  isApplyingCrop = false;
  cropSourceUrl: string | null = null;
  cropError: string | null = null;
  cropZoom = 1;
  cropOffsetX = 0;
  cropOffsetY = 0;
  cropImageNaturalWidth = 0;
  cropImageNaturalHeight = 0;

  private cropImageElement: HTMLImageElement | null = null;
  private activeCropPointerId: number | null = null;
  private cropDragOriginX = 0;
  private cropDragOriginY = 0;
  private cropDragStartOffsetX = 0;
  private cropDragStartOffsetY = 0;
  private lastPreparedFile: File | null = null;

  get isCropDragging(): boolean {
    return this.activeCropPointerId !== null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['visible'] && !changes['sourceFile']) {
      return;
    }

    if (!this.visible || !this.sourceFile) {
      this.clearCropState();
      return;
    }

    if (this.lastPreparedFile === this.sourceFile) {
      return;
    }

    this.prepareSourceFile(this.sourceFile);
  }

  ngOnDestroy(): void {
    this.clearCropState();
  }

  onCropImageLoad(event: Event): void {
    const image = event.target as HTMLImageElement | null;
    if (!image) {
      this.cropError = 'Failed to load the selected image.';
      return;
    }

    this.cropImageElement = image;
    this.cropImageNaturalWidth = Number(image.naturalWidth || image.width || 0);
    this.cropImageNaturalHeight = Number(image.naturalHeight || image.height || 0);
    this.cropError = null;
    this.resetCropTransform();
    this.queueCropClamp();
  }

  onCropZoomChange(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const nextZoom = Number(input?.value ?? this.cropZoom);
    this.cropZoom = this.clampNumber(nextZoom, 1, 4);
    this.clampCropOffsets();
  }

  onCropWheel(event: WheelEvent): void {
    if (!this.cropImageNaturalWidth || !this.cropImageNaturalHeight) {
      return;
    }

    event.preventDefault();
    const direction = event.deltaY < 0 ? 0.08 : -0.08;
    this.cropZoom = this.clampNumber(this.cropZoom + direction, 1, 4);
    this.clampCropOffsets();
  }

  startCropDrag(event: PointerEvent): void {
    if (!this.cropImageNaturalWidth || !this.cropImageNaturalHeight) {
      return;
    }

    this.activeCropPointerId = event.pointerId;
    this.cropDragOriginX = event.clientX;
    this.cropDragOriginY = event.clientY;
    this.cropDragStartOffsetX = this.cropOffsetX;
    this.cropDragStartOffsetY = this.cropOffsetY;

    const target = event.currentTarget as HTMLElement | null;
    target?.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  }

  onCropDrag(event: PointerEvent): void {
    if (this.activeCropPointerId !== event.pointerId) {
      return;
    }

    this.cropOffsetX = this.cropDragStartOffsetX + (event.clientX - this.cropDragOriginX);
    this.cropOffsetY = this.cropDragStartOffsetY + (event.clientY - this.cropDragOriginY);
    this.clampCropOffsets();
  }

  endCropDrag(event: PointerEvent): void {
    if (this.activeCropPointerId !== event.pointerId) {
      return;
    }

    const target = event.currentTarget as HTMLElement | null;
    target?.releasePointerCapture?.(event.pointerId);
    this.activeCropPointerId = null;
  }

  resetCropTransform(): void {
    this.cropZoom = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.clampCropOffsets();
  }

  cropImageTransform(): string {
    const scale = this.cropRenderScale();
    return `translate(-50%, -50%) translate(${this.cropOffsetX}px, ${this.cropOffsetY}px) scale(${scale})`;
  }

  close(): void {
    if (this.isApplyingCrop) {
      return;
    }

    this.cancel.emit();
  }

  async applyCropSelection(): Promise<void> {
    if (!this.sourceFile || !this.cropImageElement) {
      this.cropError = 'Select an image first.';
      return;
    }

    this.isApplyingCrop = true;
    this.cropError = null;

    try {
      const croppedFile = await this.buildCroppedUploadFile();
      if (Number(croppedFile.size ?? 0) > ATTACHMENT_MAX_FILE_SIZE_BYTES) {
        this.cropError = 'Cropped image is still larger than 2MB. Please zoom in a bit more or choose a smaller image.';
        return;
      }

      this.confirm.emit(croppedFile);
    } catch (error: any) {
      this.cropError = error?.message || 'Failed to crop image.';
    } finally {
      this.isApplyingCrop = false;
    }
  }

  formatFileSize(bytes: number | null | undefined): string {
    const safeBytes = Number(bytes ?? 0);
    if (!safeBytes) {
      return '0 KB';
    }
    if (safeBytes < 1024 * 1024) {
      return `${Math.max(1, Math.round(safeBytes / 1024))} KB`;
    }
    return `${(safeBytes / (1024 * 1024)).toFixed(safeBytes >= 10 * 1024 * 1024 ? 0 : 1)} MB`;
  }

  private prepareSourceFile(file: File): void {
    this.clearCropState();
    this.lastPreparedFile = file;
    this.cropSourceUrl = window.URL.createObjectURL(file);
    this.cropZoom = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.cropError = null;
    this.queueCropClamp();
  }

  private clearCropState(): void {
    if (this.cropSourceUrl) {
      window.URL.revokeObjectURL(this.cropSourceUrl);
    }

    this.cropSourceUrl = null;
    this.cropError = null;
    this.cropZoom = 1;
    this.cropOffsetX = 0;
    this.cropOffsetY = 0;
    this.cropImageNaturalWidth = 0;
    this.cropImageNaturalHeight = 0;
    this.cropImageElement = null;
    this.activeCropPointerId = null;
    this.cropDragOriginX = 0;
    this.cropDragOriginY = 0;
    this.cropDragStartOffsetX = 0;
    this.cropDragStartOffsetY = 0;
    this.lastPreparedFile = null;
  }

  private queueCropClamp(): void {
    window.requestAnimationFrame(() => this.clampCropOffsets());
  }

  private cropViewportDimension(): number {
    const element = this.cropViewportRef?.nativeElement;
    const width = Number(element?.clientWidth ?? 0);
    const height = Number(element?.clientHeight ?? 0);
    const dimension = Math.min(width, height);
    return dimension > 0 ? dimension : 320;
  }

  private cropRenderScale(): number {
    const width = Number(this.cropImageNaturalWidth ?? 0);
    const height = Number(this.cropImageNaturalHeight ?? 0);
    if (!width || !height) {
      return 1;
    }

    const viewport = this.cropViewportDimension();
    const baseScale = Math.max(viewport / width, viewport / height);
    return baseScale * this.cropZoom;
  }

  private clampCropOffsets(): void {
    const width = Number(this.cropImageNaturalWidth ?? 0);
    const height = Number(this.cropImageNaturalHeight ?? 0);
    if (!width || !height) {
      return;
    }

    const viewport = this.cropViewportDimension();
    const scale = this.cropRenderScale();
    const renderedWidth = width * scale;
    const renderedHeight = height * scale;
    const maxOffsetX = Math.max(0, (renderedWidth - viewport) / 2);
    const maxOffsetY = Math.max(0, (renderedHeight - viewport) / 2);

    this.cropOffsetX = this.clampNumber(this.cropOffsetX, -maxOffsetX, maxOffsetX);
    this.cropOffsetY = this.clampNumber(this.cropOffsetY, -maxOffsetY, maxOffsetY);
  }

  private async buildCroppedUploadFile(): Promise<File> {
    if (!this.sourceFile || !this.cropImageElement) {
      throw new Error('Select an image first.');
    }

    const viewport = this.cropViewportDimension();
    const scale = this.cropRenderScale();
    if (!viewport || !scale) {
      throw new Error('Crop area is not ready yet.');
    }

    const naturalWidth = Number(this.cropImageNaturalWidth ?? 0);
    const naturalHeight = Number(this.cropImageNaturalHeight ?? 0);
    if (!naturalWidth || !naturalHeight) {
      throw new Error('Failed to read image size.');
    }

    const srcSize = Math.min(viewport / scale, naturalWidth, naturalHeight);
    const centerX = naturalWidth / 2 - (this.cropOffsetX / scale);
    const centerY = naturalHeight / 2 - (this.cropOffsetY / scale);
    const srcX = this.clampNumber(centerX - (srcSize / 2), 0, Math.max(0, naturalWidth - srcSize));
    const srcY = this.clampNumber(centerY - (srcSize / 2), 0, Math.max(0, naturalHeight - srcSize));

    let outputSize = Math.min(720, Math.max(320, Math.round(srcSize)));
    let blob = await this.renderCroppedBlob(this.cropImageElement, srcX, srcY, srcSize, outputSize);

    while (blob.size > ATTACHMENT_MAX_FILE_SIZE_BYTES && outputSize > 320) {
      outputSize = Math.max(320, Math.round(outputSize * 0.85));
      blob = await this.renderCroppedBlob(this.cropImageElement, srcX, srcY, srcSize, outputSize);
    }

    return new File([blob], this.buildCroppedFileName(this.sourceFile.name), {
      type: 'image/jpeg',
      lastModified: Date.now()
    });
  }

  private async renderCroppedBlob(
    image: HTMLImageElement,
    srcX: number,
    srcY: number,
    srcSize: number,
    outputSize: number
  ): Promise<Blob> {
    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to prepare the cropped image.');
    }

    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, outputSize, outputSize);
    context.drawImage(image, srcX, srcY, srcSize, srcSize, 0, 0, outputSize, outputSize);

    const qualities = [0.88, 0.8, 0.72, 0.64, 0.56];
    let latestBlob: Blob | null = null;

    for (const quality of qualities) {
      latestBlob = await this.canvasToBlob(canvas, quality);
      if (latestBlob.size <= ATTACHMENT_MAX_FILE_SIZE_BYTES) {
        return latestBlob;
      }
    }

    if (!latestBlob) {
      throw new Error('Failed to prepare the cropped image.');
    }

    return latestBlob;
  }

  private canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
    return new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error('Failed to export the cropped image.'));
          return;
        }
        resolve(blob);
      }, 'image/jpeg', quality);
    });
  }

  private buildCroppedFileName(originalName: string | undefined): string {
    const name = `${originalName ?? ''}`.trim();
    const rawBaseName = name.replace(/\.[^.]+$/, '').trim() || `${this.fallbackBaseName ?? ''}`.trim() || 'image';
    const sanitizedBaseName = rawBaseName
      .normalize('NFKD')
      .replace(/[^\x20-\x7E]/g, '')
      .replace(/[^a-zA-Z0-9._ -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^[.\-_\s]+|[.\-_\s]+$/g, '')
      .slice(0, 80);
    const finalBaseName = sanitizedBaseName || `${this.fallbackBaseName ?? ''}`.trim() || 'image';
    return `${finalBaseName}-cropped.jpg`;
  }

  private clampNumber(value: number, min: number, max: number): number {
    if (!Number.isFinite(value)) {
      return min;
    }
    return Math.min(max, Math.max(min, value));
  }
}
